/**
 * Normalize phone number to E.164 format (+...)
 * Handles French numbers without prefix (06... -> +336...)
 * and preserves international numbers (+52..., +49..., etc.)
 */
export function normalizePhoneNumber(phone: string): string {
  // Remove spaces, dots, dashes
  let cleaned = phone.replace(/[\s.-]/g, '');

  // Convert 00 prefix to +
  if (cleaned.startsWith('00')) {
    cleaned = `+${cleaned.slice(2)}`;
  }

  // Convert French local format 0... to +33...
  if (cleaned.startsWith('0')) {
    return `+33${cleaned.substring(1)}`;
  }

  // Clean French number starting with +330...
  if (cleaned.startsWith('+330')) {
    return `+33${cleaned.substring(4)}`;
  }

  // Already prefixed with + (international format)
  if (cleaned.startsWith('+')) {
    return cleaned;
  }

  // If entered without prefix, treat as international if long enough or default to French
  if (cleaned.length >= 7) {
    return `+${cleaned}`;
  }

  return `+33${cleaned}`;
}

/**
 * Convert E.164 phone number format to localized format for display
 * For French numbers: (+33612345678 -> 0612345678)
 * For other international numbers: preserves the international number (+52...)
 * @param phone Phone number in E.164 format
 */
export function denormalizePhoneNumber(phone: string): string {
  const cleaned = phone.replace(/[\s.-]/g, '');

  if (cleaned.startsWith('+33')) {
    return `0${cleaned.substring(3)}`;
  }

  if (cleaned.startsWith('0033')) {
    return `0${cleaned.substring(4)}`;
  }

  return cleaned;
}

/**
 * Format phone number for display with spaces
 * Works with French format (06 12 34 56 78) and international (+52 55 12 34 56 78)
 * @param phone Phone number in any format
 * @returns Formatted phone number with spaces for display
 */
export function formatPhoneForDisplay(phone: string): string {
  const localizedPhone = denormalizePhoneNumber(phone);

  if (localizedPhone.startsWith('+')) {
    const prefix = localizedPhone.slice(0, 1);
    const rest = localizedPhone.slice(1).replace(/\D/g, '');
    return `${prefix}${rest.replace(/(\d{2})(?=\d)/g, '$1 ').trim()}`;
  }

  const digitsOnly = localizedPhone.replace(/\D/g, '');
  return digitsOnly.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
}
